import { Tab } from "@lg-cfa/components";
import { ButtonTypes } from "@lg-cfa/enums";
import { PageInterface } from "@lg-cfa/types";

import apartement from "@assets/icons/appartment-red.svg";
import cellar from "@assets/icons/cellar-red.svg";
import garage from "@assets/icons/garage-red.svg";
import attic from "@assets/icons/loft-red.svg";
import otherRooms from "@assets/icons/other-rooms-red.svg";
import outbuilding from "@assets/icons/outbuilding-red.svg";
import halfOpenOrClosed from "@assets/icons/rowhouse-red.svg";
import singleHouse from "@assets/icons/single-house-red.svg";
import {
    DataFields,
    FlowTypes,
    GetTariffParameterObjectBuildingType,
    PageNames,
    RoomTypes,
} from "@enums";
import { AddressFieldInterface, YourInformationrmationInterface } from "@types";

export const addressFields: AddressFieldInterface[] = [
    {
        id: 1,
        fieldname: DataFields.propertyCity,
        label: "propertyaddress.form.fields.city",
        columnClass: "is-full",
        required: true,
        type: "select",
        typeahead: true,
        keyPress: true,
    },
    {
        id: 2,
        fieldname: DataFields.propertyStreet,
        label: "propertyaddress.form.fields.street",
        columnClass: "is-full",
        required: true,
        type: "select",
        typeahead: true,
        keyPress: true,
        dependingOn: DataFields.propertyCity,
    },
    {
        id: 3,
        fieldname: DataFields.propertyNumber,
        label: "propertyaddress.form.fields.number",
        columnClass: "is-half",
        required: true,
        type: "text",
        dependingOn: DataFields.propertyStreet,
        maxLength: 4,
        allowedKeyPress: "[0-9]",
    },
    {
        id: 4,
        fieldname: DataFields.propertyLetterBox,
        label: "propertyaddress.form.fields.letterbox",
        columnClass: "is-half",
        required: false,
        type: "text",
        dependingOn: DataFields.propertyStreet,
        maxLength: 12,
    },
];

export const yourInformationFields: YourInformationrmationInterface[] = [
    {
        id: 1,
        fieldname: DataFields.yourInformationFirstName,
        label: "yourinformation.form.firstname",
        columnClass: "is-full",
        required: true,
        type: "text",
    },
    {
        id: 2,
        fieldname: DataFields.yourInformationLastName,
        label: "yourinformation.form.lastname",
        columnClass: "is-full",
        required: true,
        type: "text",
    },
    {
        id: 3,
        fieldname: DataFields.yourInformationEmail,
        label: "yourinformation.form.email",
        columnClass: "is-full",
        required: true,
        type: "text",
        regex: new RegExp(
            "^[\\w.!#$%&'*+/=?^{|}~-]+@[a-z\\d](?:[a-z\\d-]{0,61}[a-z\\d])?(?:\\.[a-z\\d](?:[a-z\\d-]{0,61}[a-z\\d])?)*$",
        ),
        placeholder: "yourinformation.form.email.placeholder",
    },
    {
        id: 4,
        fieldname: DataFields.yourInformationPhone,
        label: "yourinformation.form.phone",
        columnClass: "is-full",
        required: false,
        type: "text",
        regex: new RegExp("^\\+?[0-9]{9,15}$"),
        placeholder: "yourinformation.form.phone.placeholder",
    },
    {
        id: 5,
        fieldname: DataFields.yourInformationQuestion,
        label: "yourinformation.form.question.title",
        columnClass: "is-full",
        required: false,
        type: "textarea",
        placeholder: "yourinformation.form.question.placeholder",
        maxLength: 1200,
    },
    {
        id: 6,
        fieldname: DataFields.yourInformationConsent,
        label: {
            text: "yourinformation.form.consent",
            variables: {
                link: "privacy",
            },
        },
        columnClass: "is-full",
        required: false,
        type: "checkbox",
    },
];

export const propertyOptions = [
    {
        id: 1,
        title: "propertydescription.type.apartment",
        value: GetTariffParameterObjectBuildingType.Apartment,
        image: apartement,
        description: "propertydescription.type.apartment.description",
    },
    {
        id: 2,
        title: "propertydescription.type.semiclosed",
        value: GetTariffParameterObjectBuildingType.HalfOpenOrClosed,
        image: halfOpenOrClosed,
        description: "propertydescription.type.semiclosed.description",
    },
    {
        id: 3,
        title: "propertydescription.type.open",
        value: GetTariffParameterObjectBuildingType.Open,
        image: singleHouse,
        description: "propertydescription.type.open.description",
    },
];

export const rooms = [
    {
        id: 1,
        fieldName: RoomTypes.basic,
        lgTag: "lg-basic",
        name: "propertyspaces.tiles.base.title",
        isDefault: true,
        tag: "propertyspaces.tiles.base.tag",
        image: singleHouse,
        description: "propertyspaces.tiles.base.description",
        type: [
            GetTariffParameterObjectBuildingType.Apartment,
            GetTariffParameterObjectBuildingType.HalfOpenOrClosed,
            GetTariffParameterObjectBuildingType.Open,
        ],
        hint: "propertyspaces.tiles.base.hint",
        hintTitle: "propertyspaces.tiles.base.hinttitle",
    },
    {
        id: 2,
        fieldName: RoomTypes.others,
        lgTag: "lg-others",
        name: "propertyspaces.tiles.other.title",
        isDefault: false,
        tag: "",
        image: otherRooms,
        description: "propertyspaces.tiles.other.description",
        type: [
            GetTariffParameterObjectBuildingType.Apartment,
            GetTariffParameterObjectBuildingType.HalfOpenOrClosed,
            GetTariffParameterObjectBuildingType.Open,
        ],
        hint: "propertyspaces.tiles.other.hint",
        hintTitle: "propertyspaces.tiles.other.hinttitle",
    },
    {
        id: 3,
        fieldName: RoomTypes.attics,
        lgTag: "lg-attics",
        name: "propertyspaces.tiles.attic.title",
        isDefault: false,
        tag: "",
        image: attic,
        description: "propertyspaces.tiles.attic.description",
        type: [
            GetTariffParameterObjectBuildingType.HalfOpenOrClosed,
            GetTariffParameterObjectBuildingType.Open,
        ],
        hint: "propertyspaces.tiles.attic.hint",
        hintTitle: "propertyspaces.tiles.attic.hinttitle",
        max: 1,
    },
    {
        id: 4,
        fieldName: RoomTypes.garages,
        lgTag: "lg-garages",
        name: "propertyspaces.tiles.garages.title",
        isDefault: false,
        tag: "",
        image: garage,
        description: "propertyspaces.tiles.garages.description",
        type: [
            GetTariffParameterObjectBuildingType.Apartment,
            GetTariffParameterObjectBuildingType.HalfOpenOrClosed,
            GetTariffParameterObjectBuildingType.Open,
        ],
        hint: "propertyspaces.tiles.garages.hint",
        hintTitle: "propertyspaces.tiles.garages.hinttitle",
        max: 1,
    },
    {
        id: 5,
        fieldName: RoomTypes.cellars,
        lgTag: "lg-cellars",
        name: "propertyspaces.tiles.cellar.title",
        isDefault: false,
        tag: "",
        image: cellar,
        description: "propertyspaces.tiles.cellar.description",
        type: [
            GetTariffParameterObjectBuildingType.Apartment,
            GetTariffParameterObjectBuildingType.HalfOpenOrClosed,
            GetTariffParameterObjectBuildingType.Open,
        ],
        hint: "propertyspaces.tiles.cellar.hint",
        hintTitle: "propertyspaces.tiles.cellar.hinttitle",
        max: 1,
    },
    {
        id: 6,
        fieldName: RoomTypes.extras,
        lgTag: "lg-extras",
        name: "propertyspaces.tiles.extra.title",
        isDefault: false,
        tag: "",
        image: outbuilding,
        description: "propertyspaces.tiles.extra.description",
        type: [
            GetTariffParameterObjectBuildingType.HalfOpenOrClosed,
            GetTariffParameterObjectBuildingType.Open,
        ],
        hint: "propertyspaces.tiles.extra.hint",
        hintTitle: "propertyspaces.tiles.extra.hinttitle",
    },
];

export const tabs: Tab[] = [
    {
        value: "1",
        label: "stepper.step1",
        name: PageNames.FlowChoice,
    },
    {
        value: "2",
        label: "stepper.step2",
        name: PageNames.InsuranceChoice,
    },
    {
        value: "3",
        label: "stepper.step3",
        name: PageNames.BrokerSearch,
    },
    {
        value: "4",
        label: "stepper.step4",
        name: PageNames.YourInformation,
    },
];

export const pagesArray: PageInterface[] = [
    {
        name: PageNames.Welcome,
        cy: "welcome-page-generic",
        flow: FlowTypes.Generic,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/start-1"
                : "/commencez-1",
        prevPage: undefined,
        isPrevPageExternal: false,
        nextPage: PageNames.FlowChoice,
        isNextPageExternal: false,
        testTag: "lg-welcome-generic",
        buttons: {
            prev: {
                text: "",
                type: undefined,
            },
            next: {
                text: "steppernavigation.start",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "",
        neededData: [],
        weTakeCareData: [],
    },
    {
        name: PageNames.FlowChoice,
        cy: "flow-choice-page-generic",
        flow: FlowTypes.Generic,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/keuze-2"
                : "/choix-2",
        prevPage: PageNames.Welcome,
        isPrevPageExternal: false,
        nextPage: PageNames.PropertyDescription,
        isNextPageExternal: false,
        testTag: "lg-flow-choice-generic",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "",
        neededData: [],
        weTakeCareData: [],
    },
    {
        name: PageNames.Welcome,
        cy: "welcome-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/start-1"
                : "/locataire/commencez-1",
        prevPage: undefined,
        isPrevPageExternal: false,
        nextPage: PageNames.PropertyDescription,
        isNextPageExternal: false,
        testTag: "lg-welcome-tenant",
        buttons: {
            prev: {
                text: "",
                type: undefined,
            },
            next: {
                text: "steppernavigation.start",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "",
        neededData: [],
        weTakeCareData: [],
    },
    {
        name: PageNames.PropertyDescription,
        cy: "property-description-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/type-3"
                : "/locataire/type-3",
        prevPage: PageNames.FlowChoice,
        isPrevPageExternal: false,
        nextPage: PageNames.PropertyAddress,
        isNextPageExternal: false,
        testTag: "lg-property-description-tenant",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "1",
        neededData: [],
        weTakeCareData: [],
    },
    {
        name: PageNames.PropertyAddress,
        cy: "property-address-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/adres-4"
                : "/locataire/addresse-4",
        prevPage: PageNames.PropertyDescription,
        isPrevPageExternal: false,
        nextPage: PageNames.PropertySpaces,
        isNextPageExternal: false,
        testTag: "lg-property-address-tenant",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "1",
        neededData: [DataFields.housingType],
        weTakeCareData: [],
    },
    {
        name: PageNames.PropertySpaces,
        cy: "property-spaces-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/plaatsen-5"
                : "/locataire/lieux-5",
        prevPage: PageNames.PropertyAddress,
        isPrevPageExternal: false,
        nextPage: PageNames.InsuranceChoice,
        isNextPageExternal: false,
        testTag: "lg-property-spaces-tenant",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "1",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.InsuranceChoice,
        cy: "insurance-choice-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/product-6"
                : "/locataire/produit-6",
        prevPage: PageNames.PropertySpaces,
        isPrevPageExternal: false,
        nextPage: PageNames.AdditionalCovers,
        isNextPageExternal: false,
        testTag: "lg-insurance-choice-tenant",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "2",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.AdditionalCovers,
        cy: "additional-covers-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/extra-verzekeringen-7"
                : "/locataire/assurances-supplementaires-7",
        prevPage: PageNames.InsuranceChoice,
        isPrevPageExternal: false,
        nextPage: PageNames.BrokerSearch,
        isNextPageExternal: false,
        testTag: "lg-additional-covers-tenant",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "2",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
            DataFields.insurance,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.BrokerSearch,
        cy: "broker-search-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/makelaar-8"
                : "/locataire/courtier-8",
        prevPage: PageNames.AdditionalCovers,
        isPrevPageExternal: false,
        nextPage: PageNames.YourInformation,
        isNextPageExternal: false,
        testTag: "lg-broker-search-tenant",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "3",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
            DataFields.insurance,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.YourInformation,
        cy: "your-information-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/gegevens-9"
                : "/locataire/donnees-9",
        prevPage: PageNames.BrokerSearch,
        isPrevPageExternal: false,
        nextPage: PageNames.ThankYou,
        isNextPageExternal: false,
        testTag: "lg-your-information-tenant",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.confirm",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "4",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
            DataFields.insurance,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.ThankYou,
        cy: "thank-you-page-tenant",
        flow: FlowTypes.Tenant,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/huurder/bedankt-10"
                : "/locataire/merci-10",
        prevPage: undefined,
        isPrevPageExternal: false,
        nextPage: PageNames.Baloise,
        isNextPageExternal: true,
        testTag: "lg-thank-you-tenant",
        buttons: {
            prev: {
                text: "",
                type: undefined,
            },
            next: {
                text: "steppernavigation.returntosite",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "",
        neededData: [DataFields.flowFinished],
        weTakeCareData: [DataFields.flowFinished],
    },
    {
        name: PageNames.Welcome,
        cy: "welcome-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/start-1"
                : "/proprietaire/commencez-1",
        prevPage: undefined,
        isPrevPageExternal: false,
        nextPage: PageNames.PropertyDescription,
        isNextPageExternal: false,
        testTag: "lg-welcome-owner",
        buttons: {
            prev: {
                text: "",
                type: undefined,
            },
            next: {
                text: "steppernavigation.start",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "",
        neededData: [],
        weTakeCareData: [],
    },
    {
        name: PageNames.PropertyDescription,
        cy: "property-description-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/type-3"
                : "/proprietaire/type-3",
        prevPage: PageNames.FlowChoice,
        isPrevPageExternal: false,
        nextPage: PageNames.PropertyAddress,
        isNextPageExternal: false,
        testTag: "lg-property-description-owner",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "1",
        neededData: [],
        weTakeCareData: [],
    },
    {
        name: PageNames.PropertyAddress,
        cy: "property-address-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/adres-4"
                : "/proprietaire/addresse-4",
        prevPage: PageNames.PropertyDescription,
        isPrevPageExternal: false,
        nextPage: PageNames.PropertySpaces,
        isNextPageExternal: false,
        testTag: "lg-property-address-owner",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "1",
        neededData: [DataFields.housingType],
        weTakeCareData: [],
    },
    {
        name: PageNames.PropertySpaces,
        cy: "property-spaces-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/plaatsen-5"
                : "/proprietaire/lieux-5",
        prevPage: PageNames.PropertyAddress,
        isPrevPageExternal: false,
        nextPage: PageNames.InsuranceChoice,
        isNextPageExternal: false,
        testTag: "lg-property-spaces-owner",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "1",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.InsuranceChoice,
        cy: "insurance-choice-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/product-6"
                : "/proprietaire/produit-6",
        prevPage: PageNames.PropertySpaces,
        isPrevPageExternal: false,
        nextPage: PageNames.AdditionalCovers,
        isNextPageExternal: false,
        testTag: "lg-insurance-choice-owner",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "2",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.AdditionalCovers,
        cy: "additional-covers-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/extra-verzekeringen-7"
                : "/proprietaire/assurances-supplementaires-7",
        prevPage: PageNames.InsuranceChoice,
        isPrevPageExternal: false,
        nextPage: PageNames.BrokerSearch,
        isNextPageExternal: false,
        testTag: "lg-additional-covers-owner",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "2",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
            DataFields.insurance,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.BrokerSearch,
        cy: "broker-search-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/makelaar-8"
                : "/proprietaire/courtier-8",
        prevPage: PageNames.AdditionalCovers,
        isPrevPageExternal: false,
        nextPage: PageNames.YourInformation,
        isNextPageExternal: false,
        testTag: "lg-broker-search-owner",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.next",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "3",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
            DataFields.insurance,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.YourInformation,
        cy: "your-information-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/gegevens-9"
                : "/proprietaire/donnees-9",
        prevPage: PageNames.BrokerSearch,
        isPrevPageExternal: false,
        nextPage: PageNames.ThankYou,
        isNextPageExternal: false,
        testTag: "lg-your-information-owner",
        buttons: {
            prev: {
                text: "steppernavigation.previous",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.confirm",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "4",
        neededData: [
            DataFields.housingType,
            DataFields.propertyStreet,
            DataFields.propertyNumber,
            DataFields.propertyPostalCode,
            DataFields.propertyCity,
            DataFields.totalRoomCount,
            DataFields.insurance,
        ],
        weTakeCareData: [],
    },
    {
        name: PageNames.ThankYou,
        cy: "thank-you-page-owner",
        flow: FlowTypes.Owner,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/eigenaar/bedankt-10"
                : "/proprietaire/merci-10",
        prevPage: undefined,
        isPrevPageExternal: false,
        nextPage: PageNames.Baloise,
        isNextPageExternal: true,
        testTag: "lg-thank-you-owner",
        buttons: {
            prev: {
                text: "",
                type: undefined,
            },
            next: {
                text: "steppernavigation.returntosite",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "",
        neededData: [DataFields.flowFinished],
        weTakeCareData: [DataFields.flowFinished],
    },
    {
        name: PageNames.TechnicalDifficulties,
        cy: "technical-difficulties-page-generic",
        flow: FlowTypes.Generic,
        url:
            process.env.GATSBY_CURRENT_LANGUAGE === "nl"
                ? "/simulatie-niet-beschikbaar"
                : "/simulation-non-disponible",
        prevPage: PageNames.Welcome,
        isPrevPageExternal: false,
        nextPage: PageNames.Welcome,
        isNextPageExternal: false,
        testTag: "lg-technical-difficulties-generic",
        buttons: {
            prev: {
                text: "steppernavigation.tryagain",
                type: ButtonTypes.link,
                icon: "nav-go-left",
            },
            next: {
                text: "steppernavigation.returntostart",
                type: ButtonTypes.btnPrimary,
                iconRight: "nav-go-right",
            },
        },
        chapter: "",
        neededData: [],
        weTakeCareData: [],
    },
];
